import { useState } from 'react';
import { X, Shield, FileText, TestTube } from 'lucide-react';

function TOSModal({ onAccept, onClose }) {
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleAccept = async () => {
    if (!agreed) return;
    
    setLoading(true);
    try {
      await onAccept();
    } catch (error) {
      console.error('Error accepting TOS:', error);
      alert('Failed to accept Terms of Service. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
      <div className="max-w-3xl w-full bg-[#070c1a]/95 backdrop-blur-xl rounded-2xl border border-white/10 shadow-[0_30px_120px_rgba(0,0,0,0.65)] flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full border border-emerald-400/20 bg-emerald-400/10 flex items-center justify-center">
              <Shield className="h-5 w-5 text-emerald-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Terms of Service</h2>
              <p className="text-sm text-white/60">Please review and accept to continue</p>
            </div>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Terms of Service Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-cyan-400" />
              <h3 className="text-lg font-semibold text-white">Terms of Service</h3>
            </div>
            <div className="text-sm text-white/70 space-y-2 pl-7">
              <p>
                Welcome to AnimeHouse. By accessing and using this platform, you agree to be bound by these Terms of Service.
              </p>
              <p>
                <strong className="text-white/90">User Conduct:</strong> You agree to use AnimeHouse respectfully and responsibly. 
                Harassment, spam, or illegal content is strictly prohibited.
              </p>
              <p>
                <strong className="text-white/90">Content:</strong> You retain ownership of content you post, but grant AnimeHouse 
                a license to display and distribute it on the platform.
              </p>
              <p>
                <strong className="text-white/90">Account Security:</strong> You are responsible for maintaining the security 
                of your account and password.
              </p>
            </div>
          </div>

          {/* Privacy Policy Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-purple-400" />
              <h3 className="text-lg font-semibold text-white">Privacy Policy</h3>
            </div>
            <div className="text-sm text-white/70 space-y-2 pl-7">
              <p>
                <strong className="text-white/90">Data Collection:</strong> We collect information you provide when registering, 
                posting reviews, and interacting with the platform.
              </p>
              <p>
                <strong className="text-white/90">Data Usage:</strong> Your data is used to provide and improve our services, 
                personalize your experience, and communicate with you.
              </p>
              <p>
                <strong className="text-white/90">Data Security:</strong> We implement industry-standard security measures to 
                protect your personal information.
              </p>
              <p>
                <strong className="text-white/90">Third Parties:</strong> We do not sell your personal information to third parties.
              </p>
            </div>
          </div>

          {/* Testing Purposes Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <TestTube className="h-5 w-5 text-yellow-400" />
              <h3 className="text-lg font-semibold text-white">Testing & Beta Agreement</h3>
            </div>
            <div className="text-sm text-white/70 space-y-2 pl-7">
              <p>
                <strong className="text-white/90">Beta Status:</strong> AnimeHouse is currently in testing phase. 
                Features may change, and occasional downtime may occur.
              </p>
              <p>
                <strong className="text-white/90">Feedback:</strong> By participating in testing, you agree to provide 
                constructive feedback to help improve the platform.
              </p>
              <p>
                <strong className="text-white/90">Data Handling:</strong> During testing, your data and interactions 
                may be monitored for quality assurance and improvement purposes.
              </p>
              <p>
                <strong className="text-white/90">No Warranty:</strong> The platform is provided "as is" during testing. 
                We make no guarantees about uptime or feature availability.
              </p>
            </div>
          </div>
        </div>

        {/* Footer with Checkbox and Actions */}
        <div className="border-t border-white/10 p-6 space-y-4">
          <label className="flex items-start gap-3 cursor-pointer group">
            <div className="relative mt-0.5">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="peer sr-only"
              />
              <div className="h-5 w-5 rounded border-2 border-white/20 bg-white/5 peer-checked:bg-emerald-500 peer-checked:border-emerald-500 transition-all flex items-center justify-center">
                {agreed && (
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
            </div>
            <span className="text-sm text-white/80 group-hover:text-white transition-colors">
              I have read and agree to the Terms of Service, Privacy Policy, and Testing Agreement
            </span>
          </label>

          <div className="flex items-center gap-3">
            <button
              onClick={handleAccept}
              disabled={!agreed || loading}
              className="flex-1 px-6 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed text-white font-semibold transition-all disabled:opacity-50"
            >
              {loading ? 'Accepting...' : 'Accept & Continue'}
            </button>
          </div>

          <p className="text-xs text-white/40 text-center">
            By accepting, you acknowledge that you have read and understood all policies
          </p>
        </div>
      </div>
    </div>
  );
}

export default TOSModal;
